// Per-medication dosage-info popup (MEDICATION_LEVELS_PLAN.md).
// Opened by the info icon next to a medication. Same modal shell as the
// interaction / active-levels modals. Shows concise reference dosing, common
// uses, and key warnings, sourced curated → openFDA → AI and clearly labeled.
// Also shows a "Food & drink" section (MEDICATION_FOOD_GUIDANCE_PLAN.md §D3).
// Also shows a drug interactions summary for this medication against the
// patient's other active medications, with a link to the full interactions modal.

import { useEffect, useState } from "react";
import type React from "react";
import type { DrugReference, FoodGuidance, InteractionSeverity } from "@medassist/shared";
import { isInformativeTiming, FOOD_TIMING_LABEL } from "@medassist/shared";
import { getDrugReference, getFoodGuidance, checkDrugInteractions, type InteractionCheckResponse, ApiError } from "../lib/api.js";
import { Skeleton } from "./Skeleton.js";
import { SourceBadge } from "./SourceBadge.js";
import { AlertTriangleIcon, UtensilsIcon, NoFoodIcon } from "./icons.js";
import { DrugInteractionsModal } from "./DrugInteractionsModal.js";
import { ModalShell } from "./ModalShell.js";

// Amber callout matching the warning marker (⚠️) used on the medication list,
// so warnings are visually identifiable the moment the info page opens.
const WARN_BOX =
  "rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 px-3 py-2";

// Food-timing icon mirrors FoodGuidanceBadge so the same icon appears here.
function foodTimingIcon(timing: string) {
  if (timing === "empty_stomach") return <NoFoodIcon size={14} />;
  if (timing === "with_food" || timing === "either") return <UtensilsIcon size={14} />;
  return null;
}

function formatMin(min: number): string {
  if (min < 60) return `${min} min`;
  const h = min / 60;
  return `${h % 1 === 0 ? h : h.toFixed(1)} h`;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 py-1.5">
      <span className="w-28 shrink-0 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  );
}

function FoodSection({ food }: { food: FoodGuidance }) {
  // Render only when something will actually show. A bare "either"/"unknown"
  // timing with no reason, no avoid, and no spacing would leave the header with
  // an empty body, so suppress the whole section in that case (FG-5).
  const showTimingRow = isInformativeTiming(food.timing) || !!food.timingReason;
  if (!showTimingRow && food.avoid.length === 0 && food.spacing.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Food &amp; drink
        </p>
        <SourceBadge
          source={food.userOverride ? "user" : food.source}
          query={food.displayName ?? food.ingredient}
        />
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {/* Skip a bare "either"/"unknown" timing — it carries no instruction.
            Keep it only when there's a reason worth reading (e.g. "food may
            reduce nausea") (FG-5). */}
        {(isInformativeTiming(food.timing) || food.timingReason) && (
          <Row
            label="Food timing"
            value={
              <span className="inline-flex items-center gap-1.5">
                {foodTimingIcon(food.timing)}
                <span>
                  {FOOD_TIMING_LABEL[food.timing]}
                  {food.timingReason && (
                    <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">
                      — {food.timingReason}
                    </span>
                  )}
                </span>
              </span>
            }
          />
        )}
        {food.avoid.length > 0 && (
          <div className="py-2">
            <div className={WARN_BOX}>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                <AlertTriangleIcon size={14} /> Avoid
              </p>
              <ul className="list-disc space-y-0.5 pl-4 text-sm text-amber-900 dark:text-amber-100">
                {food.avoid.map((a, i) => (
                  <li key={i}>
                    <strong>{a.label}</strong>
                    {a.reason && (
                      <span className="ml-1 text-xs text-amber-700 dark:text-amber-300">
                        — {a.reason}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {food.spacing.length > 0 && (
          <Row
            label="Spacing"
            value={
              <ul className="list-disc space-y-0.5 pl-4">
                {food.spacing.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            }
          />
        )}
      </div>
    </div>
  );
}

// ── Severity metadata (mirrors DrugInteractionsModal, kept local to avoid prop-drilling) ──

const SEVERITY_BADGE: Record<InteractionSeverity, string> = {
  contraindicated: "bg-red-600 text-white",
  do_not_use: "bg-orange-600 text-white",
  serious: "bg-amber-500 text-white",
  moderate: "bg-yellow-400 text-yellow-900",
  minor: "bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-200",
};

const SEVERITY_LABEL: Record<InteractionSeverity, string> = {
  contraindicated: "Contraindicated",
  do_not_use: "Do Not Use",
  serious: "Serious",
  moderate: "Moderate",
  minor: "Minor",
};

function InteractionsSection({
  drugName,
  patientId,
  patientName,
  filterBySeverity,
}: {
  drugName: string;
  patientId: string;
  patientName: string;
  filterBySeverity: InteractionSeverity[];
}) {
  const [result, setResult] = useState<InteractionCheckResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFullModal, setShowFullModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    checkDrugInteractions(patientId, undefined, undefined, [drugName])
      .then((r) => { if (!cancelled) setResult(r); })
      .catch(() => { if (!cancelled) setResult(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [drugName, patientId]);

  if (loading) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (!result) return null;

  const visible = result.warnings.filter((w) => filterBySeverity.includes(w.severity));
  const totalCount = result.warnings.length;

  // Nothing to show and no interactions at all — skip section entirely.
  if (totalCount === 0 && !result.interactionsUnavailable) return null;

  return (
    <>
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Interactions with your other medications
          </p>
        </div>

        {result.interactionsUnavailable && (
          <p className="text-xs text-amber-700 dark:text-amber-300 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-2 py-1.5">
            Interaction data temporarily unavailable.
          </p>
        )}

        {!result.interactionsUnavailable && totalCount === 0 && (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            No interactions found with your other medications.
          </p>
        )}

        {!result.interactionsUnavailable && totalCount > 0 && (
          <>
            {visible.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {totalCount} interaction{totalCount !== 1 ? "s" : ""} found — none at your configured alert level.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {visible.map((w, i) => (
                  <li key={i} className="flex gap-2 items-start text-sm">
                    <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold ${SEVERITY_BADGE[w.severity]}`}>
                      {SEVERITY_LABEL[w.severity]}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {w.drug1 === drugName ? w.drug2 : w.drug1}
                      {w.description && (
                        <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                          — {w.description}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => setShowFullModal(true)}
              className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              See full interaction details →
            </button>
          </>
        )}
      </div>

      {showFullModal && (
        <DrugInteractionsModal
          patientId={patientId}
          patientName={patientName}
          focusNames={[drugName]}
          filterBySeverity={filterBySeverity}
          onClose={() => setShowFullModal(false)}
        />
      )}
    </>
  );
}

/** One shared-ingredient caution for the opened med: an active ingredient it
 *  shares with one or more OTHER medications the patient takes. */
export interface OverlapWarning {
  /** Display form of the shared ingredient, e.g. "Acetaminophen". */
  ingredient: string;
  /** The other medication(s) that also contain it. */
  otherMeds: string[];
}

export function MedicationInfoModal({
  drugName,
  patientId,
  patientName,
  overlapWarnings,
  filterBySeverity,
  onClose,
}: {
  drugName: string;
  patientId?: string;
  patientName?: string;
  overlapWarnings?: OverlapWarning[];
  filterBySeverity?: InteractionSeverity[];
  onClose: () => void;
}) {
  const [ref, setRef] = useState<DrugReference | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [food, setFood] = useState<FoodGuidance | null>(null);
  const [foodLoading, setFoodLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDrugReference(drugName, patientId)
      .then((r) => {
        if (!cancelled) setRef(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load info");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [drugName]);

  useEffect(() => {
    let cancelled = false;
    setFoodLoading(true);
    getFoodGuidance(drugName, patientId)
      .then((g) => {
        if (!cancelled) setFood(g);
      })
      .catch(() => {
        // Food section is optional — silently hide on error.
      })
      .finally(() => {
        if (!cancelled) setFoodLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [drugName, patientId]);

  const hasAnyData =
    ref &&
    (ref.genericName ||
      (ref.brandNames?.length ?? 0) > 0 ||
      (ref.activeIngredients?.length ?? 0) > 0 ||
      (ref.allergenFlags?.length ?? 0) > 0 ||
      ref.typicalAdultDose ||
      ref.maxSingleMg != null ||
      ref.maxDailyMg != null ||
      ref.minIntervalMin != null ||
      ref.halfLifeHours != null ||
      ref.maxDurationRecommended ||
      (ref.commonUses?.length ?? 0) > 0 ||
      (ref.keyWarnings?.length ?? 0) > 0 ||
      ref.notes);

  const headerActions = ref && (
    <SourceBadge
      source={ref.source}
      query={ref.genericName ?? drugName}
      prefix={null}
      className="shrink-0"
    />
  );

  const footer = (
    <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
      <strong>General reference only.</strong> Dosing shown is typical adult information and may
      not match what was prescribed for you. Always follow your prescription label and your
      pharmacist or clinician's instructions.
    </p>
  );

  return (
    <ModalShell
      title={drugName}
      subtitle="Common dosage information"
      headerActions={headerActions}
      footer={footer}
      size="lg"
      onClose={onClose}
    >
          {/* Shared-ingredient caution — independent of the reference fetch, shown
              first because it's the most safety-relevant thing on the page. */}
          {overlapWarnings && overlapWarnings.length > 0 && (
            <div className={WARN_BOX + " mb-3"}>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                <AlertTriangleIcon size={14} /> Also in your other medications
              </p>
              <ul className="list-disc space-y-0.5 pl-4 text-sm text-amber-900 dark:text-amber-100">
                {overlapWarnings.map((o, i) => (
                  <li key={i}>
                    Contains <strong>{o.ingredient}</strong>, also in {o.otherMeds.join(", ")}.
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">
                Taking both together adds up — watch your combined daily total of {" "}
                {overlapWarnings.map((o) => o.ingredient).join(", ")}.
              </p>
            </div>
          )}

          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {!loading && error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && ref && (
            <>
              {!hasAnyData && (
                <p className="mt-4 text-center text-sm text-gray-400 dark:text-gray-500">
                  No reference dosing information could be found for this medication.
                </p>
              )}

              {hasAnyData && (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {ref.genericName && <Row label="Generic name" value={ref.genericName} />}
                  {(ref.brandNames?.length ?? 0) > 0 && (
                    <Row label="Brand names" value={ref.brandNames!.join(", ")} />
                  )}
                  {(ref.activeIngredients?.length ?? 0) > 0 && (
                    <Row
                      label="Ingredients"
                      value={
                        <ul className="space-y-0.5">
                          {ref.activeIngredients!.map((ing, i) => (
                            <li key={i}>
                              {ing.displayName ?? ing.name}
                              {ing.strength && (
                                <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">
                                  {ing.strength}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      }
                    />
                  )}
                  {ref.typicalAdultDose && <Row label="Typical dose" value={ref.typicalAdultDose} />}
                  {ref.maxSingleMg != null && (
                    <Row label="Max single" value={`${ref.maxSingleMg} mg`} />
                  )}
                  {ref.maxDailyMg != null && (
                    <Row label="Max daily" value={`${ref.maxDailyMg} mg`} />
                  )}
                  {ref.minIntervalMin != null && (
                    <Row label="Min interval" value={`every ${formatMin(ref.minIntervalMin)}`} />
                  )}
                  {ref.halfLifeHours != null && (
                    <Row
                      label="Half-life"
                      value={
                        ref.halfLifeHours < 1
                          ? `${Math.round(ref.halfLifeHours * 60)} min`
                          : ref.halfLifeHours % 1 === 0
                          ? `${ref.halfLifeHours} h`
                          : `${ref.halfLifeHours.toFixed(1)} h`
                      }
                    />
                  )}
                  {ref.maxDurationRecommended && (
                    <Row label="Max duration" value={ref.maxDurationRecommended} />
                  )}
                  {(ref.commonUses?.length ?? 0) > 0 && (
                    <Row label="Common uses" value={ref.commonUses!.join(", ")} />
                  )}
                  {(ref.keyWarnings?.length ?? 0) > 0 && (
                    <div className="py-2">
                      <div className={WARN_BOX}>
                        <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                          <AlertTriangleIcon size={14} /> Cautions
                        </p>
                        <ul className="list-disc space-y-0.5 pl-4 text-sm text-amber-900 dark:text-amber-100">
                          {ref.keyWarnings!.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  {(ref.allergenFlags?.length ?? 0) > 0 && (
                    <Row
                      label="May contain"
                      value={
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {ref.allergenFlags!.join(", ")}
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            Common allergens found among the inactive ingredients.
                          </span>
                        </span>
                      }
                    />
                  )}
                  {ref.notes && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{ref.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Food & drink section — parallel fetch, silently hidden when no guidance */}
              {foodLoading && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-4 w-full" />
                </div>
              )}
              {!foodLoading && food && <FoodSection food={food} />}

              {/* Drug interactions with other patient meds — only when we have a patientId */}
              {patientId && patientName && filterBySeverity && (
                <InteractionsSection
                  drugName={drugName}
                  patientId={patientId}
                  patientName={patientName}
                  filterBySeverity={filterBySeverity}
                />
              )}
            </>
          )}
    </ModalShell>
  );
}
